import { Box, Button, Group, Stack, Text } from "@mantine/core"
import { modals } from "@mantine/modals"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import groupBy from "lodash/groupBy"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { MarkdownEditor, ModalControls } from "@/components/controls"
import { AppLoader, AppTitle, Markdown, Tags } from "@/components/utils"
import { useAPIResources, useAuth } from "@/hooks"
import type { TTag, TUser } from "@/types"
import { api, dateToSign, tagsAPI, userAPI } from "@/utils"
import { BlockSkeleton, EditButton, Panel } from "./utils"

export function ProfileDetails({ user }: { user: TUser }) {
  const { t } = useTranslation()
  const {
    data: details = {
      user_id: "",
      user_info: {
        intro: "",
        quote: "",
      },
      tags: [],
    },
    isLoading,
  } = useQuery({
    queryKey: ["details", user.id],
    queryFn: () => userAPI.details(user.id),
  })
  const { user: curUser } = useAuth()
  const editable = curUser?.id === user.id
  const intro = details?.user_info.intro
  const quote = details?.user_info.quote
  const tags = [...(details?.tags || [])]

  if (user.birthday)
    tags.push({
      id: dateToSign(new Date(user.birthday)),
      order: 0,
      category: "zodiac",
      display: "",
    } as TTag)

  if (!editable && !intro && !tags.length) return null

  return (
    <Panel
      block={!isLoading}
      xl={{ order: 2 }}
      lg={{
        gridRowStart: 2,
        gridColumnEnd: "span 2",
      }}
      md={{ gridRowStart: 2, gridColumnEnd: "span 1" }}
      sm={{ gridColumnEnd: "span 2" }}
    >
      {isLoading ? (
        <BlockSkeleton />
      ) : (
        <Stack gap="sm">
          {intro || editable ? (
            <Box>
              <Group justify="space-between">
                <AppTitle>{t("Intro")}</AppTitle>
                {editable && (
                  <EditButton title={t("Edit Intro")} pos="inherit" modalProps={{ size: "lg" }}>
                    <InfoEdit data={intro || ""} type="intro" userId={details?.user_id as string} />
                  </EditButton>
                )}
              </Group>
              {intro ? (
                <Markdown>{intro || ""}</Markdown>
              ) : (
                <Text my="lg" c="dimmed">
                  {t("Enter your info")}
                </Text>
              )}
            </Box>
          ) : null}

          {quote || editable ? (
            <Box>
              <Group justify="space-between">
                <AppTitle>{t("Facts about yourself")}</AppTitle>
                {editable && (
                  <EditButton title={t("Edit Facts")} pos="inherit" modalProps={{ size: "lg" }}>
                    <InfoEdit data={quote || ""} type="quote" userId={details?.user_id as string} />
                  </EditButton>
                )}
              </Group>
              {quote ? (
                <Markdown>{quote || ""}</Markdown>
              ) : (
                <Text my="lg" c="dimmed">
                  {t("Enter your info")}
                </Text>
              )}
            </Box>
          ) : null}

          <Group justify="space-between">
            <AppTitle>{t("Tags")}</AppTitle>
            {editable && (
              <EditButton title={t("Edit Tags")} pos="inherit" modalProps={{ size: "lg" }}>
                <TagsEdit tags={tags} userId={details?.user_id as string} />
              </EditButton>
            )}
          </Group>
          <Tags tags={tags.map((t) => t.id)} />
        </Stack>
      )}
    </Panel>
  )
}
interface IProps {
  data: string
  type: "intro" | "quote"
  userId: string
}

function InfoEdit({ data, type, userId }: IProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { mutate } = useMutation({
    mutationFn: (value: string) => api.put(`/people/user/${userId}`, { [type]: value }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["details", userId] })
    },
  })

  const [value, setValue] = useState(data)

  return (
    <Stack>
      <MarkdownEditor
        placeholder={t("Enter your {{type}}", { type: t(type) })}
        value={value}
        onChange={(value) => setValue(value)}
      />
      <ModalControls
        onSubmit={() => {
          modals.closeAll()
          mutate(value)
        }}
      />
    </Stack>
  )
}

function TagsEdit({ tags, userId }: { tags: TTag[]; userId: string }) {
  const { t } = useTranslation("tags")
  const queryClient = useQueryClient()
  const { mutate } = useMutation({
    mutationFn: (tags: TTag[]) => api.put(`/people/user/tags`, { tags_ids: tags.map((t) => t.id) }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["details", userId] })
    },
  })

  const [value, setValue] = useState<TTag[]>(tags.filter((t) => t.category !== "zodiac"))
  const { data: { data } = {}, isLoading } = useAPIResources(tagsAPI, {
    where: {
      category: {
        $in: ["work-style", "personal-info", "pets", "food", "sports"],
      },
    },
  })
  const allTags = groupBy(data, "category")

  if (isLoading) return <AppLoader />
  if (!allTags) return null

  return (
    <Stack>
      {Object.keys(allTags).map((category) => (
        <Box key={category}>
          <Text c="dimmed" mb="xs">
            {category.toUpperCase()}
          </Text>
          <Group>
            {allTags[category]?.map((tag) => {
              const isSelected = value.some((t) => t.id === tag.id)
              return (
                <Button
                  key={tag.id}
                  size="compact-md"
                  variant={isSelected ? "filled" : "outline"}
                  onClick={() => {
                    if (isSelected) {
                      setValue(value.filter((t) => t.id !== tag.id))
                    } else {
                      setValue([...value, tag])
                    }
                  }}
                >
                  {t(tag.id)}
                </Button>
              )
            })}
          </Group>
        </Box>
      ))}
      <ModalControls
        onSubmit={() => {
          modals.closeAll()
          mutate(value)
        }}
      />
    </Stack>
  )
}
